import { useState } from 'react';
import { signIn, confirmSignIn } from 'aws-amplify/auth';
import '../styles/theme.css';

export default function Login({ onSignIn }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('signIn');

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { isSignedIn, nextStep } = await signIn({
        username: email.trim(),
        password,
      });
      if (isSignedIn) {
        onSignIn();
      } else if (nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setStep('newPasswordRequired');
      } else {
        setError('Unexpected sign-in state: ' + nextStep?.signInStep);
      }
    } catch (err) {
      setError(err.message || 'Sign in failed.');
    } finally {
      setLoading(false);
    }
  }

  async function handleNewPassword(e) {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const { isSignedIn } = await confirmSignIn({ challengeResponse: newPassword });
      if (isSignedIn) {
        onSignIn();
      } else {
        setError('Password updated but sign-in did not complete. Please try again.');
        setStep('signIn');
      }
    } catch (err) {
      setError(err.message || 'Failed to set new password.');
    } finally {
      setLoading(false);
    }
  }

  if (step === 'newPasswordRequired') {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <span style={styles.logo} aria-hidden="true">D</span>
          <h1 style={styles.title}>DICOMVii</h1>
          <p style={styles.subtitle}>A new password is required for your account.</p>

          <form onSubmit={handleNewPassword} style={styles.form}>
            <label style={styles.label}>
              New password
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                autoComplete="new-password"
                style={styles.input}
                placeholder="Min 8 chars, upper, lower, number, symbol"
              />
            </label>

            <label style={styles.label}>
              Confirm new password
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                style={styles.input}
                placeholder="Re-enter new password"
              />
            </label>

            {error && <p style={styles.error}>{error}</p>}

            <button type="submit" disabled={loading} style={styles.button}>
              {loading ? 'Updating…' : 'Set new password'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <span style={styles.logo} aria-hidden="true">D</span>
        <h1 style={styles.title}>DICOMVii</h1>
        <p style={styles.subtitle}>Sign in to continue</p>

        <form onSubmit={handleSubmit} style={styles.form}>
          <label style={styles.label}>
            Email
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              style={styles.input}
              placeholder="you@example.com"
            />
          </label>

          <label style={styles.label}>
            Password
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              style={styles.input}
              placeholder="••••••••"
            />
          </label>

          {error && <p style={styles.error}>{error}</p>}

          <button type="submit" disabled={loading} style={styles.button}>
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
}

const styles = {
  page: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: 'var(--color-bg-light, #f5f5f5)',
    fontFamily: '"Quicksand", system-ui, sans-serif',
  },
  card: {
    backgroundColor: 'var(--color-bg-white, #fff)',
    borderRadius: 'var(--radius-default, 6px)',
    boxShadow: '0 2px 16px rgba(0,0,0,0.10)',
    padding: '2.5rem',
    width: '100%',
    maxWidth: '380px',
    border: '1px solid var(--color-border-light, #ddd)',
  },
  logo: {
    display: 'block',
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    backgroundColor: 'var(--color-primary, #F88D2D)',
    margin: '0 auto 1rem',
    lineHeight: '48px',
    textAlign: 'center',
    fontSize: '1.4rem',
    color: '#fff',
    fontWeight: 700,
  },
  title: {
    margin: '0 0 0.2rem',
    fontSize: '1.5rem',
    fontWeight: 700,
    color: 'var(--color-primary, #F88D2D)',
    textAlign: 'center',
  },
  subtitle: {
    margin: '0 0 2rem',
    color: 'var(--color-muted, #666)',
    fontSize: '0.875rem',
    textAlign: 'center',
  },
  form: { display: 'flex', flexDirection: 'column', gap: '1.1rem' },
  label: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.35rem',
    fontSize: '0.875rem',
    fontWeight: 600,
    color: 'var(--color-text, #333)',
  },
  input: {
    padding: '0.6rem 0.75rem',
    border: '1px solid var(--color-border, #ddd)',
    borderRadius: 'var(--radius-default, 6px)',
    fontSize: '0.95rem',
    outline: 'none',
    fontFamily: '"Quicksand", system-ui, sans-serif',
    backgroundColor: 'var(--color-bg-white, #fff)',
    color: 'var(--color-text, #333)',
    transition: 'border-color 0.15s',
  },
  error: {
    margin: 0,
    padding: '0.6rem 0.75rem',
    backgroundColor: 'var(--color-bg-lighter, #f9f0f0)',
    border: '1px solid var(--color-danger, #d9534f)',
    borderRadius: 'var(--radius-default, 6px)',
    color: 'var(--color-danger, #d9534f)',
    fontSize: '0.875rem',
  },
  button: {
    padding: '0.7rem',
    backgroundColor: 'var(--color-primary, #F88D2D)',
    color: 'var(--color-primary-contrast, #fff)',
    border: 'none',
    borderRadius: 'var(--radius-default, 6px)',
    fontSize: '1rem',
    fontWeight: 700,
    cursor: 'pointer',
    marginTop: '0.25rem',
    fontFamily: '"Quicksand", system-ui, sans-serif',
    transition: 'opacity 0.15s',
  },
};
